import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

const scriptOrigin = (() => {
  const scriptUrl = document.currentScript?.src
  if (!scriptUrl) return window.location.origin

  return new URL(scriptUrl).origin
})()

const riveLoaderUrl = new URL("/public/rive-canvas-lite-loader.js", scriptOrigin).href

export class CoinsAnimation {
  constructor(canvasId, sectionId, baseSpeed = 0.8, startTrigger = "top 0%", endTrigger = "bottom top") {
    this.canvasId = canvasId
    this.sectionId = sectionId
    this.rive = null
    this.canvas = null
    this.speedProperty = null
    this.baseSpeed = baseSpeed
    this.currentSpeed = baseSpeed
    this.targetSpeed = baseSpeed
    this.lastScrollTime = Date.now()
    this.observer = null
    this.isVisible = false
    this.animationFrameId = null
    this.startTrigger = startTrigger
    this.endTrigger = endTrigger
    this.isDestroyed = false
    this.scrollTrigger = null
  }

  init() {
    this.isDestroyed = false
    gsap.registerPlugin(ScrollTrigger)
    this.canvas = document.getElementById(this.canvasId)
    if (!this.canvas) {
      console.warn(`Canvas #${this.canvasId} not found`)
      return false
    }

    this.setupHighDPICanvas()
    this.setupRive().catch((error) => {
      console.warn(`Failed to initialize Rive canvas #${this.canvasId}`, error)
    })
    this.setupIntersectionObserver()
    return true
  }

  setupHighDPICanvas() {
    this.canvas.style.width = "100%"
    this.canvas.style.height = "100%"
    this.canvas.style.display = "block"
    this.canvas.style.objectFit = "contain"
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting

          if (this.rive) {
            if (entry.isIntersecting) {
              this.rive.play()
            } else {
              this.rive.pause()
            }
          }
        })
      },
      {
        threshold: 0.1,
      }
    )

    this.observer.observe(this.canvas)
  }

  async setupRive() {
    const { loadRive } = await import(riveLoaderUrl)
    const rive = await loadRive()

    if (this.isDestroyed || !this.canvas) return

    this.rive = new rive.Rive({
      src: "https://cdn.prod.website-files.com/68da6f24db5fedbdab2e7d56/68e1788e590408f8a05d1655_threshold_10.riv",
      canvas: this.canvas,
      layoutScaleFactor: 1,
      artboard: "main",
      stateMachines: "stateMachine",
      autoplay: true,
      autoBind: true,
      useOffscreenRenderer: true,
      layout: new rive.Layout({
        fit: rive.Fit.Contain,
        alignment: "center",
      }),
      onLoad: () => {
        if (this.isDestroyed || !this.rive) return

        this.rive.resizeDrawingSurfaceToCanvas()

        let vmi = this.rive.viewModelInstance
        this.speedProperty = vmi.number("speed")
        this.speedProperty.value = this.baseSpeed

        this.setupScrollTrigger()
        this.startSpeedLerp()
      },
    })
  }

  setupScrollTrigger() {
    this.scrollTrigger = ScrollTrigger.create({
      trigger: `#${this.sectionId}`,
      start: this.startTrigger,
      end: this.endTrigger,
      scrub: 1,
      // markers: true,
      onUpdate: (self) => {
        const velocity = self.getVelocity()

        if (Math.abs(velocity) > 10) {
          this.lastScrollTime = Date.now()

          const velocityMultiplier = 0.01
          this.targetSpeed = this.baseSpeed + (velocity * velocityMultiplier)

          const minSpeed = -3
          const maxSpeed = 3
          this.targetSpeed = Math.max(minSpeed, Math.min(maxSpeed, this.targetSpeed))
        }
      },
    })
  }

  startSpeedLerp() {
    const updateSpeed = () => {
      if (!this.speedProperty || !this.isVisible) {
        this.animationFrameId = requestAnimationFrame(updateSpeed)
        return
      }

      const timeSinceLastScroll = Date.now() - this.lastScrollTime
      const idleThreshold = 100

      if (timeSinceLastScroll > idleThreshold) {
        this.targetSpeed = this.baseSpeed
      }

      const lerpFactor = 0.1
      this.currentSpeed += (this.targetSpeed - this.currentSpeed) * lerpFactor

      this.speedProperty.value = this.currentSpeed

      this.animationFrameId = requestAnimationFrame(updateSpeed)
    }

    updateSpeed()
  }

  destroy() {
    this.isDestroyed = true

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.rive) {
      this.rive.cleanup()
      this.rive = null
    }

    if (this.scrollTrigger) {
      this.scrollTrigger.kill()
      this.scrollTrigger = null
    }
  }
}
